import * as React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import {
  Form,
  FormRow,
  NumericInputContainer,
  SelectContainer,
} from 'components/inputs'
import Button from 'components/Button'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import {
  FinalReturnAmountsDialogViewModel,
  StateAmountEntry,
  YearAmountEntry,
} from './FinalReturnAmountsDialogViewModel'
import { US_STATES } from 'consts/us-states'
import styles from './FinalReturnAmountsDialog.sass'

interface Props {
  vm: FinalReturnAmountsDialogViewModel
}

export default observer(function FinalReturnAmountsDialog({ vm }: Props) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Final Return Amounts</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <div
            className={styles.signHint}
            data-test="final-return-amounts-hint"
          >
            Enter amounts as: Refund (+) / Owed (−)
          </div>
          <Form>
            {vm.yearEntries.map((entry, index) => (
              <TaxYearSection
                key={entry.id}
                entry={entry}
                index={index}
                yearOptions={vm.taxYearOptions}
                onRemove={() => vm.removeYearEntry(index)}
              />
            ))}

            <FormRow>
              <Button
                type="secondary"
                size="wide-slim"
                onClick={vm.addYearEntry}
                data-test="final-return-amounts-add-year"
              >
                Add Year
              </Button>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Cancel
          </Button>
          <Button
            type="primary"
            data-test="final-return-amounts-submit-button"
            loading={vm.submit.pending}
            disabled={vm.submit.pending || vm.hasIncompleteEntries}
            onClick={vm.submit}
          >
            Save &amp; Mark Filed
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface TaxYearSectionProps {
  entry: YearAmountEntry
  index: number
  yearOptions: [string, string][]
  onRemove: () => void
}

const TaxYearSection = observer(function TaxYearSection({
  entry,
  index,
  yearOptions,
  onRemove,
}: TaxYearSectionProps) {
  return (
    <div
      className={styles.yearBlock}
      data-test={`final-return-amounts-year-${index}`}
    >
      <div className={styles.yearHeader}>
        <div className={styles.yearHeaderField}>
          <SelectContainer
            model={entry}
            field="taxYear"
            items={yearOptions}
            label="Tax year"
            placeholder="Select a tax year"
            data-test={`final-return-amounts-tax-year-${index}`}
          />
        </div>
        <div className={styles.yearHeaderField}>
          <NumericInputContainer
            model={entry}
            precision={2}
            field="federalAmount"
            name={`final-return-amounts-federal-amount-${index}`}
            label="Federal amount"
            data-test={`final-return-amounts-federal-amount-${index}`}
          />
        </div>
        <div className={styles.stateEntryRemove}>
          <IconButton
            onClick={onRemove}
            dataTest={`final-return-amounts-remove-year-${index}`}
          >
            <Icon name="close" />
          </IconButton>
        </div>
      </div>

      {entry.stateEntries.map((stateEntry, stateIndex) => (
        <StateEntryItem
          key={stateEntry.id}
          entry={stateEntry}
          yearIndex={index}
          index={stateIndex}
          onRemove={() => entry.removeStateEntry(stateIndex)}
        />
      ))}

      <FormRow>
        <Button
          type="secondary"
          size="wide-slim"
          onClick={entry.addStateEntry}
          data-test={`final-return-amounts-add-state-${index}`}
        >
          Add State
        </Button>
      </FormRow>
    </div>
  )
})

interface StateEntryItemProps {
  entry: StateAmountEntry
  yearIndex: number
  index: number
  onRemove: () => void
}

const StateEntryItem = observer(function StateEntryItem({
  entry,
  yearIndex,
  index,
  onRemove,
}: StateEntryItemProps) {
  return (
    <FormRow>
      <div className={styles.stateEntryRow}>
        <div className={styles.stateEntryField}>
          <SelectContainer
            model={entry}
            field="state"
            items={US_STATES}
            label="State"
            placeholder="Select a state"
            data-test={`final-return-amounts-state-name-${yearIndex}-${index}`}
          />
        </div>
        <div className={styles.stateEntryField}>
          <NumericInputContainer
            model={entry}
            precision={2}
            field="amount"
            name={`final-return-amounts-state-amount-${yearIndex}-${index}`}
            label="Amount"
            data-test={`final-return-amounts-state-amount-${yearIndex}-${index}`}
          />
        </div>
        <div className={styles.stateEntryRemove}>
          <IconButton
            onClick={onRemove}
            dataTest={`final-return-amounts-remove-state-${yearIndex}-${index}`}
          >
            <Icon name="close" />
          </IconButton>
        </div>
      </div>
    </FormRow>
  )
})
